// Dashboard Page - Fixed null safety for averageConfidence and percent
import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  GridItem,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Progress,
  Badge,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts'
import { useBexco } from '../contexts/BexcoContext'
import axios from 'axios'

const DashboardPage: React.FC = () => {
  const { stats, setStats, isLoading, setIsLoading } = useBexco()
  const [error, setError] = useState('')

  // Dashboard version: 1.1.0 - Fixed null safety
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // 통계 데이터 로드
  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true)
        const response = await axios.get('/graph/stats')
        //console.log('[Dashboard] 통계 데이터 수신:', response.data)
        //console.log('[Dashboard] dailyQueries:', response.data.dailyQueries)
        setStats(response.data)
        setError('')
      } catch (error) {
        console.error('Failed to load stats:', error)
        setError('통계 데이터를 불러오는데 실패했습니다.')
      } finally {
        setIsLoading(false)
      }
    }

    // 초기 로드만 실행 (자동 새로고침 제거)
    loadStats()
  }, [setStats, setIsLoading])

  // 샘플 데이터 (실제 API 연동 전까지 사용)
  const sampleStats = {
    totalQueries: 47,
    totalFaqs: 23,
    averageConfidence: 0.87,
    buildingDistribution: {
      '1전시장': 15,
      '2전시장': 12,
      '컨벤션센터': 10,
      '기타': 10,
    },
    queryTypesDistribution: {
      '공간 검색': 30,
      '일반 문의': 17,
    },
    dailyQueries: [
      { date: '10월 18일', count: 0 },
      { date: '10월 19일', count: 0 },
      { date: '10월 20일', count: 0 },
      { date: '10월 21일', count: 0 },
      { date: '10월 22일', count: 0 },
      { date: '10월 23일', count: 41 },
      { date: '10월 24일', count: 6 },
    ],
    hourlyQueries: [
      { hour: '00시', count: 8 },
      { hour: '01시', count: 1 },
      { hour: '03시', count: 2 },
      { hour: '04시', count: 2 },
      { hour: '05시', count: 3 },
      { hour: '07시', count: 17 },
      { hour: '08시', count: 3 },
      { hour: '23시', count: 15 },
    ],
  }

  const currentStats = stats || sampleStats

  // 디버깅: 현재 통계 데이터 확인 (v1.1.0)
  //console.log('[Dashboard v1.1.0] currentStats:', currentStats)
  //console.log('[Dashboard] currentStats.dailyQueries:', currentStats.dailyQueries)

  // 차트 색상 (v1.2.0 - 누리마루 보라색 추가)
  const COLORS = ['#3182ce', '#38a169', '#d69e2e', '#e53e3e', '#805AD5']
  const CHART_VERSION = '1.2.0' // Force rebuild trigger

  const buildingData = Object.entries(currentStats.buildingDistribution || {}).map(([name, value]) => ({
    name,
    value,
  }))

  const queryTypeData = Object.entries(currentStats.queryTypesDistribution || {}).map(([name, value]) => ({
    name,
    value,
  }))

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="bexco.500" />
          <Text color="gray.500">통계 데이터를 불러오는 중...</Text>
        </VStack>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <Box>
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" color="bexco.700" mb={2}>
              관리자 대시보드
            </Heading>
            <Text color="gray.600">
              AI 챗봇 시스템의 실시간 현황과 통계를 확인하세요.
            </Text>
          </Box>
          {/*<Badge colorScheme="green" fontSize="sm" px={3} py={1}>
            실시간 업데이트
          </Badge>*/}
        </HStack>
      </Box>

      {/* 주요 통계 카드 */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(4, 1fr)' }} gap={6}>
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">총 쿼리 수</StatLabel>
                <StatNumber fontSize="3xl" color="blue.600">{(currentStats.totalQueries || 0).toLocaleString()}</StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  <StatArrow type="increase" />
                  지난 주 대비 12% 증가
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">오늘 쿼리 수</StatLabel>
                <StatNumber fontSize="3xl" color="orange.600">{currentStats.todayQueries || 0}</StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">
                  <StatArrow type="increase" />
                  금일 검색 횟수
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg="purple.500" border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="white">평균 신뢰도</StatLabel>
                <StatNumber fontSize="4xl" color="white" fontWeight="bold">
                  {((currentStats.averageConfidence || 0) * 100).toFixed(1)}%
                </StatNumber>
                <StatHelpText fontSize="xs" color="purple.100">
                  최근 7일 전체 평균
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg="teal.500" border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="white">평균 처리시간</StatLabel>
                <StatNumber fontSize="4xl" color="white" fontWeight="bold">
                  {currentStats.averageProcessingTime || 0}ms
                </StatNumber>
                <StatHelpText fontSize="xs" color="teal.100">
                  최근 7일 전체 평균
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* 차트 영역 - 2열 그리드 */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
        {/* 일일 쿼리 트렌드 */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">일일 쿼리 트렌드</Heading>
            </CardHeader>
            <CardBody>
              {currentStats.dailyQueries && currentStats.dailyQueries.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart 
                    data={currentStats.dailyQueries}
                    margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      allowDecimals={false}
                      label={{ value: '쿼리 수', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                      labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#3182ce" 
                      strokeWidth={2}
                      dot={{ fill: '#3182ce', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 8 }}
                      name="쿼리 수"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box textAlign="center" py={10}>
                  <Text color="gray.500">데이터가 없습니다.</Text>
                </Box>
              )}
            </CardBody>
          </Card>
        </GridItem>

        {/* 쿼리 타입 분포 */}
        <GridItem>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">쿼리 타입 분포</Heading>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={queryTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {queryTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* 건물별 쿼리 분포 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">건물별 쿼리 분포</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={8} alignItems="center">
            {/* 파이 차트 */}
            <GridItem>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={buildingData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {buildingData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </GridItem>

            {/* 프로그레스 바 리스트 */}
            <GridItem>
              <VStack spacing={4} align="stretch">
                {buildingData.map((item, index) => {
                  const maxValue = Math.max(...buildingData.map(d => d.value))
                  const percentage = (item.value / maxValue) * 100
                  return (
                    <Box key={index}>
                      <HStack justify="space-between" mb={2}>
                        <HStack>
                          <Box w="12px" h="12px" bg={COLORS[index % COLORS.length]} borderRadius="sm" />
                          <Text fontSize="sm" fontWeight="medium">{item.name}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">{item.value}건</Text>
                      </HStack>
                      <Progress 
                        value={percentage} 
                        colorScheme={
                          index === 0 ? 'blue' : 
                          index === 1 ? 'green' : 
                          index === 2 ? 'yellow' : 
                          index === 3 ? 'red' : 'purple'
                        }
                        size="md"
                        borderRadius="md"
                      />
                    </Box>
                  )
                })}
              </VStack>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      {/* 시스템 상태 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">시스템 상태</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
            <VStack align="start" spacing={2}>
              <Text fontWeight="medium">AI 워크플로우</Text>
              <Badge colorScheme="green" size="lg">정상 운영</Badge>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontWeight="medium">Google Gemini API</Text>
              <Badge colorScheme="green" size="lg">연결됨</Badge>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontWeight="medium">ChromaDB</Text>
              <Badge colorScheme="green" size="lg">정상</Badge>
            </VStack>
          </Grid>
        </CardBody>
      </Card>

    </VStack>
  )
}

export default DashboardPage
